/**
 * Moves Layer Strategy Implementation
 *
 * Implements layer-specific logic for Moves (M) layer processing.
 * Moves layer handles both image and video files for choreography, dance, and movement assets.
 */

import {
  MOVES_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../../../constants/apiMetadataConstraints';
import {
  BaseLayerStrategy,
  LayerFieldConfig,
  ValidationResult,
} from './LayerStrategy';

export interface MovesLayerMetadata {
  layerType: 'moves';

  // Core move information
  moveName?: string;
  moveType?:
    | 'Dance'
    | 'Choreography'
    | 'Action'
    | 'Gesture'
    | 'Sport'
    | 'Exercise';
  description?: string;

  // Movement characteristics
  danceStyle?: string[];
  movementComplexity?:
    | 'Beginner'
    | 'Intermediate'
    | 'Advanced'
    | 'Professional';
  intensity?: 'Low' | 'Medium' | 'High' | 'Extreme';
  duration?: number; // seconds for videos

  // Technical dance details
  tempo?: number; // BPM if synced to music
  rhythm?: string;
  formation?: string; // solo, duo, group, etc.
  technique?: string[];

  // Body movement analysis
  bodyParts?: string[]; // which body parts are primarily involved
  coordination?: 'Simple' | 'Moderate' | 'Complex';
  balance?: 'Static' | 'Dynamic' | 'Acrobatic';
  flexibility?: 'Low' | 'Medium' | 'High';

  // Spatial characteristics
  spaceUsage?: 'Minimal' | 'Moderate' | 'Extensive';
  direction?: string[]; // forward, backward, sideways, etc.
  level?: string[]; // high, medium, low
  pathways?: string[]; // straight, curved, zigzag, etc.

  // Performance context
  musicGenre?: string[];
  performance?: boolean; // is this a performance piece
  interactive?: boolean; // can be learned/copied
  difficulty?: number; // 1-10 scale

  // AI-generated analysis
  energy?: 'Calm' | 'Moderate' | 'Energetic' | 'Explosive';
  mood?: string[];
  expressiveness?: number; // 0-1
  athleticism?: number; // 0-1
  artistry?: number; // 0-1

  // Video-specific (if applicable)
  frameRate?: number;
  resolution?: string;
  cameraAngle?: string[];
  slowMotion?: boolean;

  // Social and educational
  popularityScore?: number;
  learningTime?: number; // estimated minutes to learn
  teachingPoints?: string[];
}

export class MovesLayerStrategy extends BaseLayerStrategy {
  readonly layerCode = 'M';
  readonly layerName = 'Moves';
  readonly layerDescription =
    'Choreography, dance moves, and movement sequences';

  getRequiredFields(): string[] {
    // Updated to match backend API requirements (MovesMetadataRequest)
    // Only fields with @NotBlank annotation are required
    return ['tempo', 'moveName', 'danceStyle', 'difficulty'];
  }

  getOptionalFields(): string[] {
    return [
      'description',
      'danceStyle',
      'movementComplexity',
      'intensity',
      'duration',
      'tempo',
      'rhythm',
      'formation',
      'technique',
      'bodyParts',
      'coordination',
      'balance',
      'flexibility',
      'spaceUsage',
      'direction',
      'level',
      'pathways',
      'musicGenre',
      'performance',
      'interactive',
      'difficulty',
      'energy',
      'mood',
      'expressiveness',
      'athleticism',
      'artistry',
      'frameRate',
      'resolution',
      'cameraAngle',
      'slowMotion',
      'popularityScore',
      'learningTime',
      'teachingPoints',
    ];
  }

  getFieldConfig(): LayerFieldConfig {
    return {
      required: this.getRequiredFields(),
      optional: this.getOptionalFields(),
      computed: [
        'duration',
        'energy',
        'mood',
        'expressiveness',
        'athleticism',
        'artistry',
        'frameRate',
        'resolution',
      ],
      inherited: ['danceStyle', 'musicGenre', 'technique'],
    };
  }

  extractMetadataFromAI(aiResponse: any): MovesLayerMetadata {
    const layerMetadata =
      aiResponse.data?.layerMetadata ||
      aiResponse.layerMetadata ||
      aiResponse.movesMetadata ||
      {};

    return {
      layerType: 'moves',

      // Core information
      moveName: layerMetadata.moveName || aiResponse.moveName || '',
      moveType: this.normalizeMoveType(
        layerMetadata.moveType || aiResponse.moveType
      ),
      description: layerMetadata.description || aiResponse.description || '',

      // Movement characteristics
      danceStyle: this.normalizeArray(
        layerMetadata.danceStyle || aiResponse.danceStyle
      ),
      movementComplexity: this.normalizeComplexity(
        layerMetadata.movementComplexity || aiResponse.movementComplexity
      ),
      intensity: this.normalizeIntensity(
        layerMetadata.intensity || aiResponse.intensity
      ),
      duration: layerMetadata.duration || aiResponse.duration,

      // Technical details
      tempo: layerMetadata.tempo || aiResponse.tempo,
      rhythm: layerMetadata.rhythm || aiResponse.rhythm,
      formation: layerMetadata.formation || aiResponse.formation,
      technique: this.normalizeArray(
        layerMetadata.technique || aiResponse.technique
      ),

      // Body movement analysis from AI
      bodyParts: this.normalizeArray(
        layerMetadata.bodyParts || aiResponse.bodyParts
      ),
      coordination: this.normalizeCoordination(
        layerMetadata.coordination || aiResponse.coordination
      ),
      balance: this.normalizeBalance(
        layerMetadata.balance || aiResponse.balance
      ),
      flexibility: this.normalizeFlexibility(
        layerMetadata.flexibility || aiResponse.flexibility
      ),

      // Spatial characteristics
      spaceUsage: this.normalizeSpaceUsage(
        layerMetadata.spaceUsage || aiResponse.spaceUsage
      ),
      direction: this.normalizeArray(
        layerMetadata.direction || aiResponse.direction
      ),
      level: this.normalizeArray(layerMetadata.level || aiResponse.level),
      pathways: this.normalizeArray(
        layerMetadata.pathways || aiResponse.pathways
      ),

      // Performance context
      musicGenre: this.normalizeArray(
        layerMetadata.musicGenre || aiResponse.musicGenre
      ),
      performance: layerMetadata.performance || aiResponse.performance,
      interactive: layerMetadata.interactive || aiResponse.interactive,
      difficulty:
        layerMetadata.difficulty ||
        aiResponse.difficulty ||
        layerMetadata.complexity ||
        aiResponse.complexity,

      // AI-generated analysis
      energy: this.normalizeEnergy(layerMetadata.energy || aiResponse.energy),
      mood: this.normalizeArray(layerMetadata.mood || aiResponse.mood),
      expressiveness: layerMetadata.expressiveness || aiResponse.expressiveness,
      athleticism: layerMetadata.athleticism || aiResponse.athleticism,
      artistry: layerMetadata.artistry || aiResponse.artistry,

      // Video technical details
      frameRate:
        layerMetadata.frameRate || aiResponse.frameRate || aiResponse.fps,
      resolution: layerMetadata.resolution || aiResponse.resolution,
      cameraAngle: this.normalizeArray(
        layerMetadata.cameraAngle || aiResponse.cameraAngle
      ),
      slowMotion: layerMetadata.slowMotion || aiResponse.slowMotion,

      // Educational
      learningTime: layerMetadata.learningTime || aiResponse.learningTime,
      teachingPoints: this.normalizeArray(
        layerMetadata.teachingPoints || aiResponse.teachingPoints
      ),
    };
  }

  enhanceWithDefaults(metadata: MovesLayerMetadata): MovesLayerMetadata {
    return {
      ...this.getDefaultMetadata(),
      ...metadata,
      layerType: 'moves',

      // Ensure required fields have defaults
      moveName: metadata.moveName || 'Untitled Move',
      moveType: metadata.moveType || 'Dance',

      // Normalize arrays
      danceStyle: this.normalizeArray(metadata.danceStyle),
      technique: this.normalizeArray(metadata.technique),
      bodyParts: this.normalizeArray(metadata.bodyParts),
      direction: this.normalizeArray(metadata.direction),
      level: this.normalizeArray(metadata.level),
      pathways: this.normalizeArray(metadata.pathways),
      musicGenre: this.normalizeArray(metadata.musicGenre),
      mood: this.normalizeArray(metadata.mood),
      cameraAngle: this.normalizeArray(metadata.cameraAngle),
      teachingPoints: this.normalizeArray(metadata.teachingPoints),

      // Default values
      movementComplexity: metadata.movementComplexity || 'Intermediate',
      intensity: metadata.intensity || 'Medium',
      energy: metadata.energy || 'Moderate',
      interactive: metadata.interactive ?? true, // default to interactive
    };
  }

  generateTags(metadata: MovesLayerMetadata, description?: string): string[] {
    const tags = new Set<string>();

    // Basic layer tags
    tags.add('moves');
    tags.add('movement');
    tags.add('choreography');

    // Move type tags
    if (metadata.moveType) {
      tags.add(metadata.moveType.toLowerCase());
      switch (metadata.moveType) {
        case 'Dance':
          tags.add('dance');
          tags.add('dancing');
          break;
        case 'Choreography':
          tags.add('choreo');
          tags.add('routine');
          break;
        case 'Action':
          tags.add('action');
          tags.add('dynamic');
          break;
        case 'Gesture':
          tags.add('gesture');
          tags.add('expression');
          break;
        case 'Sport':
          tags.add('athletic');
          tags.add('sports');
          break;
        case 'Exercise':
          tags.add('fitness');
          tags.add('workout');
          break;
      }
    }

    // Dance style tags
    if (metadata.danceStyle && metadata.danceStyle.length > 0) {
      metadata.danceStyle.forEach(style => {
        tags.add(style.toLowerCase().replace(/\s+/g, '-'));
        tags.add(`${style.toLowerCase()}-dance`);
      });
    }

    // Complexity tags
    if (metadata.movementComplexity) {
      tags.add(metadata.movementComplexity.toLowerCase());
      switch (metadata.movementComplexity) {
        case 'Beginner':
          tags.add('easy');
          tags.add('simple');
          break;
        case 'Advanced':
        case 'Professional':
          tags.add('difficult');
          tags.add('expert');
          break;
      }
    }

    // Intensity tags
    if (metadata.intensity) {
      tags.add(`${metadata.intensity.toLowerCase()}-intensity`);
      switch (metadata.intensity) {
        case 'Low':
          tags.add('gentle');
          tags.add('relaxed');
          break;
        case 'High':
        case 'Extreme':
          tags.add('intense');
          tags.add('powerful');
          break;
      }
    }

    // Energy tags
    if (metadata.energy) {
      tags.add(`${metadata.energy.toLowerCase()}-energy`);
    }

    // Formation tags
    if (metadata.formation) {
      tags.add(metadata.formation.toLowerCase());
      if (metadata.formation.toLowerCase().includes('solo')) {
        tags.add('individual');
      } else if (metadata.formation.toLowerCase().includes('group')) {
        tags.add('group');
        tags.add('ensemble');
      }
    }

    // Body parts tags
    if (metadata.bodyParts && metadata.bodyParts.length > 0) {
      metadata.bodyParts.forEach(part => {
        tags.add(part.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Music genre tags
    if (metadata.musicGenre && metadata.musicGenre.length > 0) {
      metadata.musicGenre.forEach(genre => {
        tags.add(`${genre.toLowerCase()}-music`);
        tags.add(genre.toLowerCase());
      });
    }

    // Mood tags
    if (metadata.mood && metadata.mood.length > 0) {
      metadata.mood.forEach(m => {
        tags.add(`${m.toLowerCase()}-mood`);
        tags.add(m.toLowerCase());
      });
    }

    // Space usage tags
    if (metadata.spaceUsage) {
      tags.add(`${metadata.spaceUsage.toLowerCase()}-space`);
    }

    // Level tags
    if (metadata.level && metadata.level.length > 0) {
      metadata.level.forEach(l => {
        tags.add(`${l.toLowerCase()}-level`);
      });
    }

    // Technique tags
    if (metadata.technique && metadata.technique.length > 0) {
      metadata.technique.forEach(tech => {
        tags.add(tech.toLowerCase().replace(/\s+/g, '-'));
      });
    }

    // Performance tags
    if (metadata.performance) {
      tags.add('performance');
      tags.add('stage');
    }

    if (metadata.interactive) {
      tags.add('learnable');
      tags.add('tutorial');
    }

    // Technical tags (for videos)
    if (metadata.slowMotion) {
      tags.add('slow-motion');
      tags.add('detailed');
    }

    // Difficulty tags
    if (metadata.difficulty) {
      if (metadata.difficulty <= 3) {
        tags.add('easy');
      } else if (metadata.difficulty >= 8) {
        tags.add('expert');
      }
    }

    // Athletic characteristics
    if (metadata.athleticism && metadata.athleticism > 0.7) {
      tags.add('athletic');
      tags.add('physical');
    }

    if (metadata.artistry && metadata.artistry > 0.7) {
      tags.add('artistic');
      tags.add('expressive');
    }

    return Array.from(tags).slice(0, 30);
  }

  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  } {
    const categories = {
      primary: [] as string[],
      secondary: [] as string[],
      descriptive: [] as string[],
    };

    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();

      // Primary tags - type and style
      if (
        lowerTag.includes('dance') ||
        lowerTag.includes('choreography') ||
        lowerTag.includes('move') ||
        ['hip-hop', 'ballet', 'jazz', 'contemporary', 'salsa'].includes(
          lowerTag
        )
      ) {
        categories.primary.push(tag);
      }
      // Secondary tags - complexity and characteristics
      else if (
        lowerTag.includes('complexity') ||
        lowerTag.includes('intensity') ||
        lowerTag.includes('energy') ||
        lowerTag.includes('difficulty')
      ) {
        categories.secondary.push(tag);
      }
      // Descriptive tags - specific details
      else {
        categories.descriptive.push(tag);
      }
    });

    return categories;
  }

  validateBusinessRules(metadata: MovesLayerMetadata): ValidationResult {
    const errors: any[] = [];
    const warnings: any[] = [];

    // OpenAPI MovesMetadataRequest: string maxLength 255, arrays max 10
    if (metadata.moveName) {
      const msg = validateStringMaxLength(metadata.moveName, MOVES_METADATA.stringMaxLength, 'Move name');
      if (msg) errors.push({ field: 'moveName', message: msg, severity: 'error' });
    }
    for (const [key, opts] of [
      ['bodyParts', MOVES_METADATA.bodyParts],
      ['signatureMoves', MOVES_METADATA.signatureMoves],
      ['techniquesRequired', MOVES_METADATA.techniquesRequired],
      ['propsNeeded', MOVES_METADATA.propsNeeded],
    ] as const) {
      const arr = (metadata as any)[key];
      if (arr && Array.isArray(arr)) {
        const msg = validateArrayLength(arr, opts, key);
        if (msg) errors.push({ field: key, message: msg, severity: 'error' });
        else {
          const itemMsg = validateArrayItems(arr, MOVES_METADATA.stringMaxLength, key);
          if (itemMsg) errors.push({ field: key, message: itemMsg, severity: 'error' });
        }
      }
    }

    // Business rule: Move name should be meaningful
    if (metadata.moveName && metadata.moveName.trim().length < 3) {
      warnings.push({
        field: 'moveName',
        message: 'Move name should be at least 3 characters long',
        suggestion: 'Provide a more descriptive move name',
      });
    }

    // Business rule: Should have movement characteristics
    if (
      !metadata.danceStyle?.length &&
      !metadata.technique?.length &&
      !metadata.bodyParts?.length
    ) {
      warnings.push({
        field: 'danceStyle',
        message:
          'Move should have at least one movement characteristic (dance style, technique, or body parts)',
        suggestion: 'Add dance style, technique, or body parts information',
      });
    }

    // Business rule: Duration should be reasonable for videos
    if (metadata.duration) {
      if (metadata.duration < 2) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very short (< 2 seconds)',
          suggestion: 'Verify the video duration',
        });
      } else if (metadata.duration > 300) {
        warnings.push({
          field: 'duration',
          message: 'Video duration is very long (> 5 minutes)',
          suggestion: 'Consider breaking into shorter move segments',
        });
      }
    }

    // Business rule: Difficulty should be 1-10
    if (
      metadata.difficulty &&
      (metadata.difficulty < 1 || metadata.difficulty > 10)
    ) {
      warnings.push({
        field: 'difficulty',
        message: 'Difficulty should be a value between 1 and 10',
        suggestion:
          'Provide a difficulty rating from 1 (easiest) to 10 (hardest)',
      });
    }

    // Business rule: AI scores should be 0-1
    const aiFields = ['expressiveness', 'athleticism', 'artistry'];
    aiFields.forEach(field => {
      if (
        metadata[field as keyof MovesLayerMetadata] &&
        ((metadata[field as keyof MovesLayerMetadata] as number) < 0 ||
          (metadata[field as keyof MovesLayerMetadata] as number) > 1)
      ) {
        warnings.push({
          field,
          message: `${field} should be a value between 0 and 1`,
          suggestion: 'Provide a decimal value between 0 and 1',
        });
      }
    });

    // Business rule: Learning time should be reasonable
    if (
      metadata.learningTime &&
      (metadata.learningTime < 1 || metadata.learningTime > 480)
    ) {
      warnings.push({
        field: 'learningTime',
        message: 'Learning time should be between 1 minute and 8 hours',
        suggestion: 'Verify the estimated learning time',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  getDefaultMetadata(): MovesLayerMetadata {
    return {
      layerType: 'moves',
      moveName: '',
      moveType: 'Dance',
      description: '',
      danceStyle: [],
      technique: [],
      bodyParts: [],
      direction: [],
      level: [],
      pathways: [],
      musicGenre: [],
      mood: [],
      cameraAngle: [],
      teachingPoints: [],
      movementComplexity: 'Intermediate',
      intensity: 'Medium',
      energy: 'Moderate',
      interactive: true,
    };
  }

  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  } {
    return {
      // Moves are unique - don't inherit names
      moveName: 'never',
      moveType: 'never',

      // Could inherit style characteristics from collections
      danceStyle: 'merge',
      musicGenre: 'merge',
      technique: 'merge',
      mood: 'merge',
    };
  }

  private normalizeArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [];
  }

  private normalizeMoveType(value: any): MovesLayerMetadata['moveType'] {
    if (!value) return 'Dance';

    const normalized = String(value).toLowerCase();

    if (normalized.includes('choreo')) return 'Choreography';
    if (normalized.includes('action') || normalized.includes('stunt'))
      return 'Action';
    if (normalized.includes('gesture') || normalized.includes('expression'))
      return 'Gesture';
    if (normalized.includes('sport') || normalized.includes('athletic'))
      return 'Sport';
    if (
      normalized.includes('exercise') ||
      normalized.includes('fitness') ||
      normalized.includes('workout')
    )
      return 'Exercise';

    return 'Dance';
  }

  private normalizeComplexity(
    value: any
  ): MovesLayerMetadata['movementComplexity'] {
    if (!value) return 'Intermediate';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('beginner') ||
      normalized.includes('easy') ||
      normalized.includes('basic')
    )
      return 'Beginner';
    if (normalized.includes('advanced') || normalized.includes('expert'))
      return 'Advanced';
    if (normalized.includes('professional') || normalized.includes('master'))
      return 'Professional';

    return 'Intermediate';
  }

  private normalizeIntensity(value: any): MovesLayerMetadata['intensity'] {
    if (!value) return 'Medium';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('low') ||
      normalized.includes('gentle') ||
      normalized.includes('easy')
    )
      return 'Low';
    if (
      normalized.includes('high') ||
      normalized.includes('intense') ||
      normalized.includes('hard')
    )
      return 'High';
    if (normalized.includes('extreme') || normalized.includes('maximum'))
      return 'Extreme';

    return 'Medium';
  }

  private normalizeCoordination(
    value: any
  ): MovesLayerMetadata['coordination'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('simple') || normalized.includes('basic'))
      return 'Simple';
    if (normalized.includes('complex') || normalized.includes('advanced'))
      return 'Complex';

    return 'Moderate';
  }

  private normalizeBalance(value: any): MovesLayerMetadata['balance'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('static') || normalized.includes('stable'))
      return 'Static';
    if (normalized.includes('acrobatic') || normalized.includes('aerial'))
      return 'Acrobatic';

    return 'Dynamic';
  }

  private normalizeFlexibility(value: any): MovesLayerMetadata['flexibility'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('low') || normalized.includes('minimal'))
      return 'Low';
    if (normalized.includes('high') || normalized.includes('extreme'))
      return 'High';

    return 'Medium';
  }

  private normalizeSpaceUsage(value: any): MovesLayerMetadata['spaceUsage'] {
    if (!value) return undefined;

    const normalized = String(value).toLowerCase();

    if (normalized.includes('minimal') || normalized.includes('small'))
      return 'Minimal';
    if (normalized.includes('extensive') || normalized.includes('large'))
      return 'Extensive';

    return 'Moderate';
  }

  private normalizeEnergy(value: any): MovesLayerMetadata['energy'] {
    if (!value) return 'Moderate';

    const normalized = String(value).toLowerCase();

    if (
      normalized.includes('calm') ||
      normalized.includes('peaceful') ||
      normalized.includes('slow')
    )
      return 'Calm';
    if (
      normalized.includes('energetic') ||
      normalized.includes('upbeat') ||
      normalized.includes('fast')
    )
      return 'Energetic';
    if (
      normalized.includes('explosive') ||
      normalized.includes('intense') ||
      normalized.includes('powerful')
    )
      return 'Explosive';

    return 'Moderate';
  }
}

// Export singleton instance
export const movesLayerStrategy = new MovesLayerStrategy();
